import { useEffect, useRef, useSyncExternalStore } from "react";

import { LocaleProvider } from "@frontend/providers";

import { BridgeContent, initBridgeStyles } from "./BridgeContent";
import { defineRWCElements } from "./defineRWCElements";
import { BridgeEvents } from "./events";
import {
  getMountPointsSnapshot,
  subscribeMountPoints,
} from "./mountPointStore";
import type { ComponentHandle, ComponentName } from "./types";

declare global {
  interface Window {
    ReactComponents: Record<string, ComponentHandle<ComponentName>>;
  }
}

// Initialize the global handle registry if not already present.
if (!window.ReactComponents) {
  window.ReactComponents = {};
}

performance.mark("bridge:evaluate");

// Runs before the first render
initBridgeStyles();

// Defines all custom elements
defineRWCElements();

performance.mark("bridge:elements-defined");

export default function Bridge() {
  const mountPoints = useSyncExternalStore(
    subscribeMountPoints,
    getMountPointsSnapshot,
  );

  // Notifies legacy code whenever the set of mounted components changes
  const mountedEls = useRef<Set<HTMLElement>>(new Set());

  useEffect(() => {
    // Only announce when elements were actually mounted or unmounted.
    const els = new Set(mountPoints.map(({ el }) => el));

    const prev = mountedEls.current;

    if (els.size === prev.size && mountPoints.every(({ el }) => prev.has(el))) {
      return;
    }

    mountedEls.current = els;

    if (mountPoints.length === 0) {
      return;
    }

    BridgeEvents.componentsReady.dispatch({
      count: mountPoints.length,
      components: { ...window.ReactComponents },
    });
  }, [mountPoints]);

  return (
    <LocaleProvider>
      <BridgeContent mountPoints={mountPoints} />
    </LocaleProvider>
  );
}
